import _React, { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import * as L from 'leaflet';
import maplibregl from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import '@maplibre/maplibre-gl-leaflet';
import { Protocol } from 'pmtiles';

// Registra o protocolo PMTiles apenas uma vez
let __pmtilesProtocolInstalled = false;
function ensurePmtilesProtocol() {
  if (!__pmtilesProtocolInstalled) {
    const protocol = new Protocol();
    // Wrapper para garantir que o SAS seja preservado/adicionado quando necessário
    maplibregl.addProtocol('pmtiles', (params, callback) => {
      try {
        const prefix = 'pmtiles://';
        let url = params && typeof params.url === 'string' ? params.url : '';
        const sas = (import.meta && import.meta.env && import.meta.env.VITE_TILE_SAS) || '';
        if (url && url.startsWith(prefix)) {
          let inner = url.slice(prefix.length);
          const hasQuery = inner.indexOf('?') !== -1;
          const query = hasQuery ? inner.split('?')[1] : '';
          const hasSig = query ? /(^|&)sig=/i.test(query) : false;
          if (sas && !hasSig) {
            const sasClean = sas.startsWith('?') ? sas.slice(1) : sas;
            if (hasQuery && query.length > 0) {
              inner += `&${sasClean}`;
            } else {
              inner += `?${sasClean}`;
            }
            url = `${prefix}${inner}`;
          }
        }
        return protocol.tile({ ...params, url }, callback);
      } catch (_) {
        return protocol.tile(params, callback);
      }
    });
    __pmtilesProtocolInstalled = true;
  }
}

// Popups de atributos em tiles desabilitados globalmente
const SHOW_TILE_POPUPS = false;

export default function MaplibreLayer({
  style,
  zIndex = 6,
  interactiveLayerId,
  popupFields = [],
  displayName,
  controllerRef,
}) {
  const map = useMap();
  // Normaliza zIndex: valores pequenos (ex.: 5,6,7) são deslocados para a faixa > 350
  const effectiveZ = (() => {
    const z = Number(zIndex);
    if (!Number.isFinite(z)) return 380;
    return z < 100 ? 350 + z : z;
  })();
  const layerRef = useRef(null);
  const mlMapRef = useRef(null);
  const onLeafletClickHandlerRef = useRef(null);
  // Refs para fallback manual (sem plugin)
  const containerRef = useRef(null);
  const onLeafletMoveRef = useRef(null);
  const onLeafletZoomRef = useRef(null);
  const onLeafletResizeRef = useRef(null);
  // Pane dedicado por instância para garantir sobreposição previsível
  const paneNameRef = useRef(null);

  useEffect(() => {
    ensurePmtilesProtocol();
    // Cria/obtém um pane exclusivo para esta instância
    try {
      if (!paneNameRef.current) {
        const base = (displayName || 'maplibre')
          .toString()
          .toLowerCase()
          .replace(/[^a-z0-9_-]+/g, '-');
        const paneName = `ml-${base}-${Math.random().toString(36).slice(2, 7)}`;
        paneNameRef.current = paneName;
        if (!map.getPane(paneName)) {
          map.createPane(paneName);
        }
        const p = map.getPane(paneName);
        if (p && p.style) p.style.zIndex = String(effectiveZ);
      }
    } catch (_) {
      /* no-op */
    }

    const hasPlugin = typeof (L && L.maplibreGL) === 'function';
    if (hasPlugin) {
      // Caminho 1: usar plugin oficial (se instalado)
      const layer = L.maplibreGL({
        style,
        attribution: false,
        interactive: false,
        pane: paneNameRef.current || 'overlayPane',
      });
      map.addLayer(layer);
      layerRef.current = layer;
      const mlMap = layer.getMaplibreMap();
      mlMapRef.current = mlMap;
      // Evita bloquear cliques no mapa (pesquisa por clique) mantendo pointer-events:none
      try {
        const container =
          typeof layer.getContainer === 'function' ? layer.getContainer() : layer._container;
        if (container && container.style) container.style.pointerEvents = 'none';
      } catch (_) {
        /* no-op */
      }
      // z-index já aplicado no pane

      if (SHOW_TILE_POPUPS) {
        const clickHandler = (e) => {
          try {
            if (!interactiveLayerId) return;
            const { lat, lng } = e.latlng;
            const pt = mlMap.project([lng, lat]);
            const feats = mlMap.queryRenderedFeatures(pt, { layers: [interactiveLayerId] });
            if (!feats || feats.length === 0) return;
            const f = feats[0];
            const rows = (popupFields || [])
              .map((key) => {
                const value =
                  f.properties && (f.properties[key] ?? f.properties[key?.toUpperCase?.()] ?? '');
                return value !== '' && value != null
                  ? `<div><strong>${key}:</strong> ${String(value)}</div>`
                  : '';
              })
              .filter(Boolean)
              .join('');
            const header = displayName
              ? `<div style="font-weight:600;margin-bottom:4px;">${displayName}</div>`
              : '';
            const html = `${header}${rows || '<em>Sem atributos</em>'}`;
            L.popup({ maxWidth: 360 }).setLatLng([lat, lng]).setContent(html).openOn(map);
          } catch (_) {
            /* no-op */
          }
        };
        map.on('click', clickHandler);
        onLeafletClickHandlerRef.current = clickHandler;
      }

      return () => {
        try {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          onLeafletClickHandlerRef.current && map.off('click', onLeafletClickHandlerRef.current);
        } catch (_) {
          /* no-op */
        }
        try {
          // eslint-disable-next-line @typescript-eslint/no-unused-expressions
          layer && map.removeLayer(layer);
        } catch (_) {
          /* no-op */
        }
      };
    }

    // Caminho 2: fallback manual (sem plugin) — ancorado no overlayPane (acima do basemap)
    const overlayPane = map.getPane(paneNameRef.current || 'overlayPane');
    const div = document.createElement('div');
    div.style.position = 'absolute';
    div.style.top = '0';
    div.style.left = '0';
    div.style.right = '0';
    div.style.bottom = '0';
    // tamanho explícito
    const mc = map.getContainer();
    div.style.width = mc.clientWidth + 'px';
    div.style.height = mc.clientHeight + 'px';
    // garantir sobreposição acima do tilePane
    div.style.zIndex = String(effectiveZ);
    div.style.pointerEvents = 'none';
    // Anexar ao overlayPane para herdar transform de pan/zoom do Leaflet
    overlayPane.appendChild(div);
    containerRef.current = div;

    const mlMap = new maplibregl.Map({
      container: div,
      style,
      interactive: false,
      attributionControl: false,
      preserveDrawingBuffer: false,
    });
    mlMapRef.current = mlMap;

    const syncFromLeaflet = () => {
      try {
        const center = map.getCenter();
        const zoom = map.getZoom() - 1; // alinhar escala Leaflet(256) x MapLibre(512)
        mlMap.jumpTo({ center: [center.lng, center.lat], zoom, bearing: 0, pitch: 0 });
      } catch (_) {
        /* no-op */
      }
    };
    const onMove = () => syncFromLeaflet();
    const onZoom = () => syncFromLeaflet();
    const onResize = () => {
      try {
        const mc2 = map.getContainer();
        if (containerRef.current) {
          containerRef.current.style.width = mc2.clientWidth + 'px';
          containerRef.current.style.height = mc2.clientHeight + 'px';
        }
        mlMap.resize();
      } catch (_) {
        /* no-op */
      }
      syncFromLeaflet();
    };
    // Sincronizar continuamente durante pan/zoom para evitar qualquer drift visual
    map.on('move', onMove);
    map.on('zoom', onZoom);
    map.on('resize', onResize);
    onLeafletMoveRef.current = onMove;
    onLeafletZoomRef.current = onZoom;
    onLeafletResizeRef.current = onResize;
    syncFromLeaflet();

    if (SHOW_TILE_POPUPS) {
      const clickHandler = (e) => {
        try {
          if (!interactiveLayerId) return;
          const { lat, lng } = e.latlng;
          const pt = mlMap.project([lng, lat]);
          const feats = mlMap.queryRenderedFeatures(pt, { layers: [interactiveLayerId] });
          if (!feats || feats.length === 0) return;
          const f = feats[0];
          const rows = (popupFields || [])
            .map((key) => {
              const value =
                f.properties && (f.properties[key] ?? f.properties[key?.toUpperCase?.()] ?? '');
              return value !== '' && value != null
                ? `<div><strong>${key}:</strong> ${String(value)}</div>`
                : '';
            })
            .filter(Boolean)
            .join('');
          const header = displayName
            ? `<div style="font-weight:600;margin-bottom:4px;">${displayName}</div>`
            : '';
          const html = `${header}${rows || '<em>Sem atributos</em>'}`;
          L.popup({ maxWidth: 360 }).setLatLng([lat, lng]).setContent(html).openOn(map);
        } catch (_) {
          /* no-op */
        }
      };
      map.on('click', clickHandler);
      onLeafletClickHandlerRef.current = clickHandler;
    }

    // show/hide pelo LayersControl
    const show = () => {
      if (containerRef.current) containerRef.current.style.display = '';
      syncFromLeaflet();
      try {
        mlMap.resize();
      } catch (_) {
        /* no-op */
      }
    };
    const hide = () => {
      if (containerRef.current) containerRef.current.style.display = 'none';
    };
    const onOverlayAdd = (e) => {
      try {
        if (controllerRef && controllerRef.current && e.layer === controllerRef.current) show();
      } catch (_) {
        /* no-op */
      }
    };
    const onOverlayRemove = (e) => {
      try {
        if (controllerRef && controllerRef.current && e.layer === controllerRef.current) hide();
      } catch (_) {
        /* no-op */
      }
    };
    map.on('overlayadd', onOverlayAdd);
    map.on('overlayremove', onOverlayRemove);
    try {
      if (controllerRef && controllerRef.current) {
        if (!map.hasLayer(controllerRef.current)) hide();
        else show();
      }
    } catch (_) {
      /* no-op */
    }

    return () => {
      try {
        map.off('click', onLeafletClickHandlerRef.current);
      } catch (_) {
        /* no-op */
      }
      try {
        map.off('move', onLeafletMoveRef.current);
      } catch (_) {
        /* no-op */
      }
      try {
        map.off('zoom', onLeafletZoomRef.current);
      } catch (_) {
        /* no-op */
      }
      try {
        map.off('resize', onLeafletResizeRef.current);
      } catch (_) {
        /* no-op */
      }
      try {
        map.off('overlayadd', onOverlayAdd);
        map.off('overlayremove', onOverlayRemove);
      } catch (_) {
        /* no-op */
      }
      try {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        mlMap && mlMap.remove();
      } catch (_) {
        /* no-op */
      }
      try {
        // eslint-disable-next-line @typescript-eslint/no-unused-expressions
        containerRef.current && containerRef.current.remove();
        containerRef.current = null;
      } catch (_) {
        /* no-op */
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [map]);

  // Atualizações de estilo (plugin ou manual)
  useEffect(() => {
    const mlMap = mlMapRef.current;
    if (!mlMap) return;
    const DEBUG = import.meta && import.meta.env && import.meta.env.VITE_DEBUG === 'true';
    try {
      const patchPaintIfPossible = () => {
        try {
          const current = mlMap.getStyle && mlMap.getStyle();
          if (!current || !current.layers || !Array.isArray(style?.layers)) return false;
          const existingIds = new Set((current.layers || []).map((l) => l.id));
          // Se houver layers novos no style que não existem no mapa, não fazer patch incremental
          const missingIds = (style.layers || [])
            .map((l) => l && l.id)
            .filter(Boolean)
            .filter((id) => !existingIds.has(id));
          if (missingIds.length > 0) {
            if (DEBUG) return false;
          }
          let patchedAny = false;
          for (const lyr of style.layers) {
            if (!lyr || !lyr.id || !existingIds.has(lyr.id)) continue;
            if (lyr.paint && typeof lyr.paint === 'object') {
              for (const [k, v] of Object.entries(lyr.paint)) {
                try {
                  mlMap.setPaintProperty(lyr.id, k, v);
                  patchedAny = true;
                } catch (_) {
                  /* no-op */
                }
              }
            }
            if (lyr.layout && typeof lyr.layout === 'object') {
              for (const [k, v] of Object.entries(lyr.layout)) {
                try {
                  mlMap.setLayoutProperty(lyr.id, k, v);
                  patchedAny = true;
                } catch (_) {
                  /* no-op */
                }
              }
            }
          }
          if (patchedAny) {
            // eslint-disable-next-line no-console
            if (DEBUG) console.debug('[MaplibreLayer] patched paint/layout for existing layers');
            try {
              // eslint-disable-next-line @typescript-eslint/no-unused-expressions
              mlMap.triggerRepaint && mlMap.triggerRepaint();
            } catch (_) {
              /* no-op */
            }
            try {
              // re-sincroniza viewport sem alterar posição
              const c = mlMap.getCenter();
              const z = mlMap.getZoom();
              mlMap.jumpTo({ center: c, zoom: z, bearing: 0, pitch: 0 });
            } catch (_) {
              /* no-op */
            }
          }
          return patchedAny;
        } catch (_) {
          return false;
        }
      };

      const applySetStyle = () => {
        try {
          if (DEBUG)
            // eslint-disable-next-line no-console
            console.debug('[MaplibreLayer] setStyle (full)', {
              displayName,
              styleLayers: style?.layers?.length,
            });
          mlMap.setStyle(style);
          // Após aplicar o style, aguardar primeiro ciclo de styledata/idle para ajustar tamanho e sincronizar viewport
          const onStyleData = () => {
            try {
              mlMap.resize();
            } catch (_) {
              /* no-op */
            }
            mlMap.off('styledata', onStyleData);
          };
          mlMap.on('styledata', onStyleData);
          const onIdle = () => {
            try {
              mlMap.resize();
            } catch (_) {
              /* no-op */
            }
            mlMap.off('idle', onIdle);
          };
          mlMap.on('idle', onIdle);
        } catch (e) {
          if (DEBUG) console.warn('[MaplibreLayer] setStyle error', e);
        }
      };

      if (mlMap.isStyleLoaded && mlMap.isStyleLoaded()) {
        // Tentar patch incremental; se não der, aplicar setStyle completo
        const patched = patchPaintIfPossible();
        if (!patched) applySetStyle();
      } else {
        const onLoad = () => {
          applySetStyle();
          mlMap.off('load', onLoad);
        };
        mlMap.on('load', onLoad);
      }
    } catch (e) {
      if (DEBUG) console.warn('[MaplibreLayer] style update exception', e);
    }
  }, [style, displayName]);

  return null;
}
